import axios from "axios";
import i18n from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  // Auth runs 100% on httpOnly Secure SameSite=None cookies set by the
  // backend on /auth/login, /auth/2fa/login-verify, /auth/verify-email and
  // /auth/refresh. The browser attaches them automatically because of
  // withCredentials. There is no client-side JWT storage (no localStorage,
  // no sessionStorage) — this is XSS-safe by design.
  withCredentials: true,
});

// Every request carries the UI language so backend AI/deterministic
// content (Claude prompts, guardrail, calculator strings) matches it.
api.interceptors.request.use((config) => {
  config.headers["X-App-Language"] = (i18n.language || "it").split("-")[0];
  return config;
});

// Expired access cookie → one shared /auth/refresh call, then the original
// request is retried. No reload, no redirect: page state survives.
// Requests sent before the last successful refresh just retry (no new rotation).
const NO_REFRESH = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout", "/auth/2fa"];
let refreshing = null;
let lastRefreshAt = 0;
api.interceptors.request.use((config) => {
  config._sentAt = Date.now();
  return config;
});
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  if (error.response?.status !== 401 || !cfg || cfg._retried || NO_REFRESH.some((p) => (cfg.url || "").startsWith(p))) {
    return Promise.reject(error);
  }
  cfg._retried = true;
  if (cfg._sentAt >= lastRefreshAt) {
    refreshing = refreshing || api.post("/auth/refresh")
      .then(() => { lastRefreshAt = Date.now(); })
      .finally(() => { refreshing = null; });
    try {
      await refreshing;
    } catch {
      return Promise.reject(error);
    }
  }
  return api(cfg);
});

export function formatApiError(detail) {
  if (detail == null) return "Si è verificato un errore.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Centralised axios error → human-readable Italian message.
// Handles 3 hidden cases that confused users: (1) backend down/network down →
// no response at all, (2) 500 with no detail body, (3) Pydantic 422 with
// nested `loc/msg` arrays. Always returns something actionable so the user
// never sees the generic "Si è verificato un errore.".
export function extractAxiosError(err, fallback = "Errore di rete") {
  // Network down / CORS blocked / backend offline
  if (!err.response) {
    if (err.code === "ERR_NETWORK") {
      return `Server non raggiungibile. Verifica la tua connessione e riprova. (${err.message || "no response"})`;
    }
    return `${fallback}: ${err.message || "nessuna risposta dal server"}`;
  }
  const { status, data } = err.response;
  if (data && data.detail) return formatApiError(data.detail);
  if (data && typeof data === "string" && data.trim()) return data;
  if (status >= 500) return `Errore server (${status}). Riprova tra qualche minuto o contatta l'assistenza.`;
  if (status === 422) return "Dati inseriti non validi. Controlla i campi.";
  return `${fallback} (HTTP ${status})`;
}

export default api;
